import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, AlertTriangle, DollarSign, MapPin, Users, Clock, Phone, Building2, Star, ArrowRight, Home, ChevronRight } from 'lucide-react';

export default function GuiaElegirCentroPage() {
  var steps = [
    {
      number: 1,
      title: "Evalúa el tipo de adicción y gravedad",
      description: "El primer paso es identificar qué sustancia o comportamiento está causando la adicción y cuánto tiempo lleva el problema.",
      tips: [
        "Adicciones a sustancias (alcohol, drogas, medicamentos)",
        "Adicciones comportamentales (juego, tecnología)",
        "Considera si hay uso de múltiples sustancias",
        "Evalúa si existen condiciones de salud mental asociadas"
      ]
    },
    {
      number: 2,
      title: "Define la modalidad de tratamiento",
      description: "Existen diferentes modalidades según las necesidades del paciente y su situación familiar/laboral.",
      tips: [
        "Residencial: Internación 24/7, ideal para casos graves",
        "Ambulatorio: Asiste durante el día, duerme en casa",
        "Semi-residencial: Combinación de ambas modalidades",
        "Online: Para seguimiento y casos leves"
      ]
    },
    {
      number: 3,
      title: "Verifica las credenciales del centro",
      description: "Un centro de rehabilitación serio debe contar con certificaciones y personal calificado.",
      tips: [
        "Autorización sanitaria vigente (SEREMI de Salud)",
        "Equipo multidisciplinario (psiquiatras, psicólogos, terapeutas)",
        "Metodología de tratamiento basada en evidencia",
        "Transparencia en sus procesos y resultados"
      ]
    },
    {
      number: 4,
      title: "Considera la ubicación y accesibilidad",
      description: "La ubicación puede influir en el éxito del tratamiento y en las visitas familiares.",
      tips: [
        "Cercanía para visitas familiares regulares",
        "Ambiente tranquilo, alejado de factores de riesgo",
        "Acceso a servicios médicos de emergencia",
        "Considera si prefieres tratamiento en tu ciudad o fuera"
      ]
    },
    {
      number: 5,
      title: "Evalúa los costos y formas de pago",
      description: "El tratamiento de adicciones es una inversión importante. Compara opciones y pregunta por facilidades.",
      tips: [
        "Solicita un desglose detallado de costos",
        "Pregunta qué incluye y qué tiene costo adicional",
        "Consulta por planes de pago o financiamiento",
        "Verifica si aceptan ISAPRE o FONASA"
      ]
    },
    {
      number: 6,
      title: "Visita el centro antes de decidir",
      description: "Nada reemplaza una visita presencial para conocer las instalaciones y al equipo.",
      tips: [
        "Agenda una visita guiada por las instalaciones",
        "Conoce al equipo terapéutico",
        "Pregunta por el programa de tratamiento específico",
        "Observa las condiciones de higiene y seguridad"
      ]
    }
  ];

  var redFlags = [
    "Promesas de curas milagrosas o rápidas",
    "Falta de transparencia en costos",
    "No permiten visitas familiares",
    "Personal sin credenciales verificables",
    "Presión para firmar contratos inmediatamente",
    "Instalaciones en mal estado o inseguras"
  ];

  var faqs = [
    {
      question: "¿Cuánto tiempo dura un tratamiento de rehabilitación?",
      answer: "La duración típica es de 6 a 12 meses de tratamiento activo, más 6 a 12 meses adicionales de seguimiento y prevención de recaídas. La fase residencial intensiva puede durar de 1 a 3 meses, seguida de tratamiento ambulatorio. La duración exacta depende de la gravedad de la adicción y la respuesta individual."
    },
    {
      question: "¿Cuánto cuesta un centro de rehabilitación en Chile?",
      answer: "Los costos varían según la modalidad. Tratamiento residencial: $350.000 a $3.000.000 CLP mensuales. Tratamiento ambulatorio: $350.000 a $600.000 CLP mensuales. Tratamiento online: desde $200.000 CLP mensuales. También existen opciones gratuitas a través de SENDA y FONASA."
    },
    {
      question: "¿Es mejor un centro residencial o ambulatorio?",
      answer: "Depende de cada caso. El tratamiento residencial es recomendado para adicciones severas, múltiples recaídas o cuando el entorno familiar no es favorable. El ambulatorio funciona bien para adicciones leves o como continuación después de la internación."
    },
    {
      question: "¿Qué pasa si el paciente no quiere ir a rehabilitación?",
      answer: "La motivación es importante pero no siempre está presente al inicio. Muchos centros ofrecen intervenciones familiares y terapia motivacional. En casos extremos, existe la posibilidad de internación involuntaria bajo supervisión médica y judicial."
    },
    {
      question: "¿Cómo sé si un centro de rehabilitación es confiable?",
      answer: "Verifica que tenga autorización sanitaria, busca opiniones de ex pacientes, visita las instalaciones, conoce al equipo profesional y pide referencias. Un centro serio será transparente con su metodología y resultados."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      
        <title>Cómo Elegir un Centro de Rehabilitación de Drogas en Chile | Guía 2026</title>
        <meta name="description" content="Guía completa para elegir el mejor centro de rehabilitación de drogas y alcohol en Chile. 6 pasos esenciales, preguntas frecuentes y señales de alerta. Actualizado 2026." />
        <meta name="keywords" content="como elegir centro rehabilitacion, mejores centros rehabilitacion chile, guia rehabilitacion drogas, que buscar centro rehabilitacion, centros rehabilitacion confiables" />
        <link rel="canonical" href="https://sinadicciones.org/como-elegir-centro-rehabilitacion" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sinadicciones.org/como-elegir-centro-rehabilitacion" />
        <meta property="og:title" content="Cómo Elegir un Centro de Rehabilitación de Drogas en Chile | Guía 2026" />
        <meta property="og:description" content="Guía completa con 6 pasos esenciales, FAQs y señales de alerta para elegir el mejor centro de rehabilitación en Chile." />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cómo Elegir un Centro de Rehabilitación en Chile | Guía 2026" />
        <meta name="twitter:description" content="6 pasos esenciales y señales de alerta." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        
        {/* HowTo Schema for AI */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "Cómo Elegir un Centro de Rehabilitación de Drogas en Chile",
          "description": "Guía paso a paso para elegir el mejor centro de rehabilitación para tratamiento de adicciones",
          "totalTime": "P1D",
          "estimatedCost": { "@type": "MonetaryAmount", "currency": "CLP", "value": "350000-3000000" },
          "step": steps.map(function(s, i) {
            return {
              "@type": "HowToStep",
              "position": i + 1,
              "name": s.title,
              "text": s.description,
              "itemListElement": s.tips.map(function(tip) {
                return { "@type": "HowToTip", "text": tip };
              })
            };
          })
        })}</script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(function(faq) {
            return {
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
            };
          })
        })}</script>
        
        {/* Breadcrumb Schema */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
            { "@type": "ListItem", "position": 2, "name": "Guías", "item": "https://sinadicciones.org/herramientas" },
            { "@type": "ListItem", "position": 3, "name": "Cómo Elegir un Centro de Rehabilitación" }
          ]
        })}</script>
      
      
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Breadcrumbs */}
        <div className="max-w-4xl mx-auto px-4 mb-8">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 flex items-center gap-1"><Home className="w-3 h-3" />Inicio</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/herramientas" className="hover:text-emerald-400">Herramientas</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-300">Cómo Elegir un Centro</span>
          </nav>
        </div>
        
        {/* Hero */}
        <div className="max-w-4xl mx-auto px-4 mb-12">
          <div className="text-center">
            <span className="inline-block px-4 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm mb-4">
              Guía Actualizada 2026
            </span>
            <h1 className="text-3xl md:text-5xl font-bold mb-6">
              Cómo Elegir un Centro de Rehabilitación de <span className="text-emerald-400">Drogas en Chile</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Guía completa con los 6 pasos esenciales para encontrar el tratamiento adecuado. 
              Basada en la experiencia de cientos de familias chilenas.
            </p>
          </div>
        </div>
        
        {/* Steps */}
        <div className="max-w-4xl mx-auto px-4 mb-16">
          <div className="space-y-6">
            {steps.map(function(step) {
              return (
                <Card key={step.number} className="bg-slate-900 border-slate-800">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-xl">
                        {step.number}
                      </div>
                      <div className="flex-1">
                        <h2 className="text-xl font-bold mb-2">{step.title}</h2>
                        <p className="text-slate-400 mb-4">{step.description}</p>
                        <ul className="space-y-2">
                          {step.tips.map(function(tip, i) {
                            return (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                                <span className="text-slate-300">{tip}</span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
        
        {/* Red Flags */}
        <div className="max-w-4xl mx-auto px-4 mb-16">
          <Card className="bg-red-500/10 border-red-500/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-bold text-red-400 mb-4">Señales de Alerta: Cuándo NO Elegir un Centro</h2>
                  <ul className="grid md:grid-cols-2 gap-3">
                    {redFlags.map(function(flag, i) {
                      return (
                        <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                          <span className="text-red-400">✗</span>
                          {flag}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* FAQs */}
        <div className="max-w-4xl mx-auto px-4 mb-16">
          <h2 className="text-2xl font-bold mb-6 text-center">Preguntas Frecuentes sobre Rehabilitación</h2>
          <div className="space-y-4">
            {faqs.map(function(faq, i) {
              return (
                <Card key={i} className="bg-slate-900 border-slate-800">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-2 text-emerald-400">{faq.question}</h3>
                    <p className="text-slate-400">{faq.answer}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
        
        {/* CTA */}
        <div className="max-w-4xl mx-auto px-4">
          <Card className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-emerald-500/30">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">¿Listo para encontrar el centro ideal?</h2>
              <p className="text-slate-400 mb-6">
                Explora nuestro directorio con más de 140 centros de rehabilitación verificados en todo Chile.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-600">
                  <Link to="/centros-de-rehabilitacion-drogas-chile">
                    <Building2 className="w-5 h-5 mr-2" />Ver Centros de Rehabilitación
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-slate-600 hover:bg-slate-800">
                  <Link to="/calculadora">
                    <DollarSign className="w-5 h-5 mr-2" />Calcular Costos
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
